/**
 * Filter System
 * Handles client-side filtering with URL params, range sliders, multiselect, and search
 */
export default class Filters {
	constructor() {
		this.isInitialized = false;
		this.activeFilters = {};
		this.initialFilters = {};
		window.filtersInstance = this;
		this.init();
	}

	init() {
		if(this.isInitialized) return;
		this.isInitialized = true;

		this.loadFiltersFromURL();
		this.initRangeSliders();
		this.initMultiselect();
		this.initButtonGroup();
		this.initTextSearch();

		document.addEventListener('click', (e) => {
			const clearBtn = e.target.closest('[data-filter-clear]');
			if(!clearBtn) return;
			e.preventDefault();
			this.clearAllFilters();
		});

		document.addEventListener('click', (e) => {
			const viewBtn = e.target.closest('[data-filter-view-results]');
			if(!viewBtn) return;
			e.preventDefault();

			// Check if we're in timeline context
			const timeline = document.querySelector('[data-filterable-container="timeline"]');
			if(timeline) {
				// Reload page with new URL params
				window.location.href = window.location.pathname + window.location.search;
			} else {
				// Close modal for other filters
				const openModal = document.querySelector('.op-modal[open]');
				if(openModal?.id && window.modalInstance) {
					window.modalInstance.close(openModal.id);
				}
			}
		});

		document.addEventListener('click', (e) => {
			const resetBtn = e.target.closest('[data-filter-reset]');
			if(!resetBtn) return;
			e.preventDefault();
			const attr = resetBtn.dataset.filterReset;
			this.resetFilter(attr);
		});

		document.addEventListener('click', (e) => {
			const trigger = e.target.closest('.filter-trigger');
			if(!trigger) return;
			setTimeout(() => {
				this.syncFiltersFromURL();
				this.initialFilters = JSON.parse(JSON.stringify(this.activeFilters));
				this.updateViewResultsButton();
				this.applyAllFilters();
			}, 100);
		});

		setTimeout(() => {
			this.applyAllFilters();
			this.initialFilters = JSON.parse(JSON.stringify(this.activeFilters));
		}, 100);
	}

	initRangeSliders() {
		const sliders = document.querySelectorAll('.filter-range');

		sliders.forEach((slider) => {
			const sliderEl = slider.querySelector('.filter-range__slider');
			const track = slider.querySelector('.filter-range__track');
			const fill = slider.querySelector('.filter-range__fill');
			const minThumb = slider.querySelector('.filter-range__thumb--min');
			const maxThumb = slider.querySelector('.filter-range__thumb--max');
			const minDisplay = slider.querySelector('.filter-range__value--min');
			const maxDisplay = slider.querySelector('.filter-range__value--max');
			const attr = slider.dataset.filterAttr;

			if(!sliderEl || !track || !minThumb || !maxThumb) return;

			const min = parseInt(sliderEl.dataset.min);
			const max = parseInt(sliderEl.dataset.max);
			let minVal = min;
			let maxVal = max;
			let activeThumb = null;

			if(this.activeFilters[attr]?.type === 'range') {
				minVal = this.activeFilters[attr].min;
				maxVal = this.activeFilters[attr].max;
			}

			const updateVisuals = () => {
				const minPercent = ((minVal - min) / (max - min)) * 100;
				const maxPercent = ((maxVal - min) / (max - min)) * 100;

				minThumb.style.left = minPercent + '%';
				maxThumb.style.left = maxPercent + '%';

				if(fill) {
					fill.style.left = minPercent + '%';
					fill.style.width = (maxPercent - minPercent) + '%';
				}

				if(minDisplay) minDisplay.textContent = minVal;
				if(maxDisplay) maxDisplay.textContent = maxVal;
			};

			const updateFilters = () => {
				if(minVal === min && maxVal === max) {
					delete this.activeFilters[attr];
				} else {
					this.activeFilters[attr] = {
						type: 'range',
						min: minVal,
						max: maxVal
					};
				}
				this.applyAllFilters();
				this.updateURL();
			};

			const getValueFromPosition = (clientX) => {
				const rect = track.getBoundingClientRect();
				const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
				return Math.round(min + percent * (max - min));
			};

			const handleStart = (e, thumb) => {
				e.preventDefault();
				activeThumb = thumb;
				thumb.classList.add('is-active');
			};

			const handleMove = (e) => {
				if(!activeThumb) return;

				const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
				const newValue = getValueFromPosition(clientX);

				if(activeThumb === minThumb) {
					minVal = Math.min(newValue, maxVal - 1);
				} else {
					maxVal = Math.max(newValue, minVal + 1);
				}

				updateVisuals();
			};

			const handleEnd = () => {
				if(activeThumb) {
					activeThumb.classList.remove('is-active');
					activeThumb = null;
					updateFilters();
				}
			};

			minThumb.addEventListener('mousedown', (e) => handleStart(e, minThumb));
			minThumb.addEventListener('touchstart', (e) => handleStart(e, minThumb));
			maxThumb.addEventListener('mousedown', (e) => handleStart(e, maxThumb));
			maxThumb.addEventListener('touchstart', (e) => handleStart(e, maxThumb));

			document.addEventListener('mousemove', handleMove);
			document.addEventListener('touchmove', handleMove);
			document.addEventListener('mouseup', handleEnd);
			document.addEventListener('touchend', handleEnd);

			updateVisuals();
		});
	}

	initMultiselect() {
		const multiselects = document.querySelectorAll('.filter-multiselect');

		multiselects.forEach((container) => {
			const attr = container.dataset.filterAttr;

			// Always query DOM fresh in case modal has moved elements
			const getSelect = () => container.querySelector('.filter-multiselect__select');
			const getChips = () => container.querySelector('.filter-multiselect__chips');

			if(!getSelect()) return;

			// Tracked values are the source of truth
			const tracked = new Set();

			const updateChips = () => {
				const chipsContainer = getChips();
				const select = getSelect();
				if(!chipsContainer) return;

				chipsContainer.innerHTML = '';

				if(tracked.size === 0) {
					chipsContainer.style.display = 'none';
					return;
				}

				chipsContainer.style.display = '';

				tracked.forEach(value => {
					const option = select?.querySelector(`option[value="${CSS.escape(value)}"]`);
					if(!option) return;

					const chip = document.createElement('button');
					chip.type = 'button';
					chip.className = 'filter-chip';
					chip.dataset.value = value;

					const text = document.createElement('span');
					text.className = 'filter-chip__text';
					text.textContent = option.textContent.trim();

					const removeBtn = document.createElement('span');
					removeBtn.className = 'icon icon-x';

					chip.appendChild(text);
					chip.appendChild(removeBtn);
					chipsContainer.appendChild(chip);

					chip.addEventListener('click', (e) => {
						e.preventDefault();
						tracked.delete(value);
						syncFromTracked();
					});
				});
			};

			const syncFromTracked = () => {
				if(tracked.size > 0) {
					this.activeFilters[attr] = {
						type: 'multiselect',
						values: Array.from(tracked)
					};
				} else {
					delete this.activeFilters[attr];
				}

				updateChips();
				this.applyAllFilters();
				this.updateURL();
			};

			const reset = () => {
				tracked.clear();
				const select = getSelect();
				if(select) select.selectedIndex = 0;
				updateChips();
			};

			const select = getSelect();
			select.addEventListener('change', () => {
				const value = select.value;
				if(!value) return;

				tracked.add(value);
				select.selectedIndex = 0;
				syncFromTracked();
			});

			// Store references on container
			container._tracked = tracked;
			container._reset = reset;
			container._updateChips = updateChips;

			// Initialize from URL
			if(this.activeFilters[attr]?.type === 'multiselect') {
				this.activeFilters[attr].values.forEach(v => tracked.add(v));
				updateChips();
			}
		});
	}

	initButtonGroup() {
		const buttonGroups = document.querySelectorAll('.filter-button-group');

		buttonGroups.forEach((container) => {
			const buttons = container.querySelectorAll('.filter-button-group__button');
			const attr = container.dataset.filterAttr;

			if(!buttons.length) return;

			if(this.activeFilters[attr]?.type === 'button-group') {
				const selectedValues = this.activeFilters[attr].values;
				buttons.forEach(btn => {
					if(selectedValues.includes(btn.dataset.value)) {
						btn.classList.add('is-active');
					}
				});
			}

			buttons.forEach(btn => {
				btn.addEventListener('click', (e) => {
					e.preventDefault();
					btn.classList.toggle('is-active');

					const activeButtons = container.querySelectorAll('.filter-button-group__button.is-active');
					const selectedValues = Array.from(activeButtons).map(b => b.dataset.value);

					if(selectedValues.length > 0) {
						this.activeFilters[attr] = {
							type: 'button-group',
							values: selectedValues
						};
					} else {
						delete this.activeFilters[attr];
					}

					this.applyAllFilters();
					this.updateURL();
				});
			});
		});
	}

	initTextSearch() {
		const searches = document.querySelectorAll('.filter-search');

		searches.forEach((container) => {
			const input = container.querySelector('.filter-search__input');
			const attr = container.dataset.filterAttr;

			if(!input) return;

			let timeout;
			input.addEventListener('input', (e) => {
				clearTimeout(timeout);
				timeout = setTimeout(() => {
					const value = e.target.value.trim();

					if(value) {
						this.activeFilters[attr] = {
							type: attr === 'all' ? 'global-search' : 'search',
							value: value.toLowerCase()
						};
					} else {
						delete this.activeFilters[attr];
					}

					this.applyAllFilters();
					this.updateURL();
				}, 300);
			});

			if(this.activeFilters[attr]) {
				input.value = this.activeFilters[attr].value;
			}
		});
	}

	applyAllFilters() {
		const containers = document.querySelectorAll('[data-filterable-container]');

		containers.forEach((container) => {
			const items = container.querySelectorAll('[data-filterable-item]');
			let visibleCount = 0;

			items.forEach((item) => {
				let shouldShow = true;

				for(const [attr, filter] of Object.entries(this.activeFilters)) {

					if(filter.type === 'global-search') {
						const allData = [];
						for(const key in item.dataset) {
							if(key !== 'filterableItem' && item.dataset[key]) {
								allData.push(item.dataset[key].toLowerCase());
							}
						}
						if(item.textContent) {
							allData.push(item.textContent.toLowerCase());
						}
						const combinedData = allData.join(' ');
						if(!this.fuzzyMatch(combinedData, filter.value)) {
							shouldShow = false;
							break;
						}
						continue;
					}

					const itemData = item.dataset[attr];

					if(!itemData) {
						if(Object.keys(this.activeFilters).length > 0) {
							shouldShow = false;
						}
						break;
					}

					if(filter.type === 'range') {
						const itemValue = parseInt(itemData);
						if(isNaN(itemValue) || itemValue < filter.min || itemValue > filter.max) {
							shouldShow = false;
							break;
						}
					}

					if(filter.type === 'multiselect' || filter.type === 'button-group') {
						const itemValues = itemData.split(',').map(v => v.trim().toLowerCase());
						// Require ALL selected values to be present (AND logic)
						const hasAllMatches = filter.values.every(filterVal =>
							itemValues.includes(filterVal.toLowerCase())
						);
						if(!hasAllMatches) {
							shouldShow = false;
							break;
						}
					}

					if(filter.type === 'search') {
						if(!this.fuzzyMatch(itemData, filter.value)) {
							shouldShow = false;
							break;
						}
					}
				}

				if(shouldShow) {
					item.style.display = '';
					item.classList.remove('is-filtered-out');
					visibleCount++;
				} else {
					item.style.display = 'none';
					item.classList.add('is-filtered-out');
				}
			});

			this.updateStatus(container, visibleCount, items.length);
		});

		// Hide year headers with no visible items (timeline only)
		document.querySelectorAll('.sk8nigs__timeline-year').forEach(yearHeader => {
			const yearValue = yearHeader.dataset.year;
			let hasVisibleItems = false;

			let nextEl = yearHeader.nextElementSibling;
			while(nextEl && !nextEl.classList.contains('sk8nigs__timeline-year')) {
				if(nextEl.classList.contains('sk8nigs__history-item') &&
					nextEl.style.display !== 'none' &&
					nextEl.dataset.year === yearValue) {
					hasVisibleItems = true;
					break;
				}
				nextEl = nextEl.nextElementSibling;
			}

			yearHeader.style.display = hasVisibleItems ? '' : 'none';
		});

		this.updateFilterButtons();
	}

	fuzzyMatch(text, search) {
		text = text.toLowerCase();
		search = search.toLowerCase();

		let searchIndex = 0;
		for(let i = 0; i < text.length && searchIndex < search.length; i++) {
			if(text[i] === search[searchIndex]) {
				searchIndex++;
			}
		}
		return searchIndex === search.length;
	}

	updateFilterButtons() {
		const filterButtons = document.querySelectorAll('.filter-trigger');
		const activeCount = Object.keys(this.activeFilters).length;

		filterButtons.forEach(button => {
			let baseText = button.textContent.replace(/\s*\(\d+\)$/, '');

			if(activeCount > 0) {
				button.textContent = `${baseText} (${activeCount})`;
			} else {
				button.textContent = baseText;
			}
		});
	}

	async fetchTimelineCount() {
		const params = new URLSearchParams();

		const container = document.querySelector('[data-filterable-container="timeline"]');
		if(!container) return 0;

		const minYear = parseInt(container.dataset.filterYearMin);
		const maxYear = parseInt(container.dataset.filterYearMax);
		const activeRange = this.activeFilters['year'];

		if(activeRange?.type === 'range') {
			params.append('year_min', activeRange.min);
			params.append('year_max', activeRange.max);
		} else if(minYear && maxYear) {
			params.append('year_min', minYear);
			params.append('year_max', maxYear);
		}

		if(this.activeFilters['type']?.type === 'button-group') {
			params.append('types', this.activeFilters['type'].values.join(','));
		}

		if(this.activeFilters['people']?.type === 'multiselect') {
			params.append('people', this.activeFilters['people'].values.join(','));
		}

		try {
			const response = await fetch(`/wp-json/sk8nigs/v1/history/count?${params.toString()}`);
			const data = await response.json();
			return data.count || 0;
		} catch(error) {
			console.error('Error fetching timeline count:', error);
			return 0;
		}
	}

	updateStatus(container, visibleCount, totalCount) {
		const statusElements = document.querySelectorAll('[data-filter-status]');
		if(!statusElements.length) return;

		const firstStatus = statusElements[0];
		let singular = firstStatus.dataset.singular;
		let plural = firstStatus.dataset.plural;

		// Check if we have type filters active - use those labels instead
		let typesLabel = null;
		const typeFilter = this.activeFilters['type'];
		if(typeFilter?.type === 'button-group' && typeFilter.values.length > 0) {
			// Look for buttons anywhere, not just in one container
			const selectedTypes = [];
			typeFilter.values.forEach(value => {
				const btn = document.querySelector(`.filter-button-group[data-filter-attr="type"] button[data-value="${value}"]`);
				if(btn) {
					selectedTypes.push(btn.textContent.trim());
				}
			});

			if(selectedTypes.length > 0) {
				// Pluralize the type names
				const pluralTypes = selectedTypes.map(type => {
					if(type === 'Video') return 'Videos';
					if(type === 'Review') return 'Reviews';
					if(type === 'Show') return 'Shows';
					if(type === 'Release') return 'Releases';
					if(type === 'Event') return 'Events';
					return type + 's';
				});

				typesLabel = pluralTypes.length > 2
					? `${pluralTypes.slice(0, -1).join(', ')}, and ${pluralTypes[pluralTypes.length - 1]}`
					: pluralTypes.join(' and ');
			}
		}

		const label = typesLabel || (visibleCount === 1 ? singular : plural);

		let statusParts = [];

		let countText = `Found <strong>${visibleCount} ${label.toLowerCase()}</strong>`;

		statusParts.push(countText);

		// Try container data attributes first (timeline), fallback to slider (video/music)
		let minYear = parseInt(container.dataset.filterYearMin);
		let maxYear = parseInt(container.dataset.filterYearMax);

		if(!minYear || !maxYear) {
			const rangeSlider = document.querySelector('.filter-range__slider');
			if(rangeSlider) {
				minYear = parseInt(rangeSlider.dataset.min);
				maxYear = parseInt(rangeSlider.dataset.max);
			}
		}

		if(minYear && maxYear) {
			const activeRange = this.activeFilters['year'];

			if(activeRange?.type === 'range') {
				statusParts.push(`from <strong>${activeRange.min}-${activeRange.max}</strong>`);
			} else {
				statusParts.push(`from <strong>${minYear}-${maxYear}</strong>`);
			}
		}

		for(const [attr, filter] of Object.entries(this.activeFilters)) {
			if(filter.type === 'range' || filter.type === 'button-group' || attr === 'type') {
				continue;
			} else if(filter.type === 'multiselect') {
				const container = document.querySelector(`[data-filter-attr="${attr}"].filter-multiselect`);
				const selectEl = container?.querySelector('select');
				if(selectEl && filter.values.length > 0) {
					const selectedLabels = filter.values.map(val => {
						const opt = selectEl.querySelector(`option[value="${CSS.escape(val)}"]`);
						return opt ? opt.textContent.trim() : val;
					});
					const boldedLabels = selectedLabels.map(name => `<strong>${name}</strong>`);
					const joined = boldedLabels.length > 2
						? `${boldedLabels.slice(0, -1).join(', ')}, and ${boldedLabels[boldedLabels.length - 1]}`
						: boldedLabels.join(' and ');

					if(attr === 'people') {
						statusParts.push(`with ${joined}`);
					} else if(attr === 'songs') {
						statusParts.push(`featuring ${joined}`);
					} else {
						statusParts.push(joined);
					}
				}
			} else if(filter.type === 'search') {
				statusParts.push(`matching <strong>"${filter.value}"</strong>`);
			} else if(filter.type === 'global-search') {
				statusParts.push(`matching <strong>"${filter.value}"</strong>`);
			}
		}

		const statusHTML = statusParts.join(' ');

		const activeFilterCount = Object.keys(this.activeFilters).length;

		statusElements.forEach(status => {
			const textDisplay = status.querySelector('.filter-status__text');

			if(textDisplay) {
				textDisplay.innerHTML = statusHTML;
			}
		});

		const clearElements = document.querySelectorAll('[data-filter-clear]');
		clearElements.forEach(clearEl => {
			const clearType = clearEl.dataset.clearType;

			if(clearType === 'button') {
				if(activeFilterCount > 0) {
					clearEl.disabled = false;
				} else {
					clearEl.disabled = true;
				}
			} else if(clearType === 'button-with-count') {
				const countEl = clearEl.querySelector('.filter-clear-button__count');
				if(countEl) {
					if(activeFilterCount > 0) {
						countEl.textContent = `(${activeFilterCount})`;
					} else {
						countEl.textContent = '';
					}
				}
			} else {
				const baseText = clearEl.dataset.clearText || 'Clear Filters';
				if(activeFilterCount > 0) {
					clearEl.textContent = ` ${baseText} (${activeFilterCount})`;
					clearEl.style.display = '';
				} else {
					clearEl.style.display = 'none';
				}
			}
		});

		this.updateViewResultsButton();
		this.updateResetLinks();
	}

	updateViewResultsButton() {
		const viewButton = document.querySelector('[data-filter-view-results]');
		if(!viewButton) return;

		const hasChanges = JSON.stringify(this.activeFilters) !== JSON.stringify(this.initialFilters);

		if(hasChanges) {
			viewButton.disabled = false;
		} else {
			viewButton.disabled = true;
		}
	}

	updateResetLinks() {
		const resetLinks = document.querySelectorAll('[data-filter-reset]');

		resetLinks.forEach(link => {
			const attr = link.dataset.filterReset;

			if(this.activeFilters[attr]) {
				link.style.display = '';
			} else {
				link.style.display = 'none';
			}
		});
	}

	resetFilter(attr) {
		delete this.activeFilters[attr];

		const rangeSlider = document.querySelector(`[data-filter-attr="${attr}"].filter-range`);
		if(rangeSlider) {
			const sliderEl = rangeSlider.querySelector('.filter-range__slider');
			const minThumb = rangeSlider.querySelector('.filter-range__thumb--min');
			const maxThumb = rangeSlider.querySelector('.filter-range__thumb--max');
			const fill = rangeSlider.querySelector('.filter-range__fill');
			const minDisplay = rangeSlider.querySelector('.filter-range__value--min');
			const maxDisplay = rangeSlider.querySelector('.filter-range__value--max');

			if(sliderEl) {
				const min = parseInt(sliderEl.dataset.min);
				const max = parseInt(sliderEl.dataset.max);

				if(minThumb) minThumb.style.left = '0%';
				if(maxThumb) maxThumb.style.left = '100%';
				if(fill) {
					fill.style.left = '0%';
					fill.style.width = '100%';
				}
				if(minDisplay) minDisplay.textContent = min;
				if(maxDisplay) maxDisplay.textContent = max;
			}
		}

		const multiselectContainer = document.querySelector(`[data-filter-attr="${attr}"].filter-multiselect`);
		if(multiselectContainer && multiselectContainer._reset) {
			multiselectContainer._reset();
		}

		const buttonGroup = document.querySelector(`[data-filter-attr="${attr}"].filter-button-group`);
		if(buttonGroup) {
			const buttons = buttonGroup.querySelectorAll('.filter-button-group__button');
			buttons.forEach(btn => btn.classList.remove('is-active'));
		}

		const search = document.querySelector(`[data-filter-attr="${attr}"].filter-search input`);
		if(search) {
			search.value = '';
		}

		this.updateURL();
		this.applyAllFilters();
		this.updateViewResultsButton();
		this.updateResetLinks();

		// Force visuals to sync after URL update
		setTimeout(() => {
			this.syncFiltersFromURL();
		}, 10);
	}

	clearAllFilters() {
		this.activeFilters = {};

		document.querySelectorAll('.filter-range').forEach((slider) => {
			const sliderEl = slider.querySelector('.filter-range__slider');
			const minThumb = slider.querySelector('.filter-range__thumb--min');
			const maxThumb = slider.querySelector('.filter-range__thumb--max');
			const fill = slider.querySelector('.filter-range__fill');
			const minDisplay = slider.querySelector('.filter-range__value--min');
			const maxDisplay = slider.querySelector('.filter-range__value--max');

			if(sliderEl) {
				const min = parseInt(sliderEl.dataset.min);
				const max = parseInt(sliderEl.dataset.max);

				if(minThumb) minThumb.style.left = '0%';
				if(maxThumb) maxThumb.style.left = '100%';
				if(fill) {
					fill.style.left = '0%';
					fill.style.width = '100%';
				}
				if(minDisplay) minDisplay.textContent = min;
				if(maxDisplay) maxDisplay.textContent = max;
			}
		});

		document.querySelectorAll('.filter-multiselect').forEach((container) => {
			if(container._reset) {
				container._reset();
			}
		});

		document.querySelectorAll('.filter-button-group__button').forEach((btn) => {
			btn.classList.remove('is-active');
		});

		document.querySelectorAll('.filter-search__input').forEach((input) => {
			input.value = '';
		});

		this.updateURL();
		this.applyAllFilters();

		this.initialFilters = {};

		const openModal = document.querySelector('.op-modal[open]');
		if(openModal?.id) {
			if(window.modalInstance) {
				window.modalInstance.close(openModal.id);
			}
		}

		// Force slider visuals to stay reset by syncing after a tick
		setTimeout(() => {
			this.syncFiltersFromURL();
		}, 10);
	}

	loadFiltersFromURL() {
		const urlParams = new URLSearchParams(window.location.search);

		urlParams.forEach((value, key) => {
			if(!key.startsWith('view_')) return;

			const attr = key.substring(5);

			const rangeEl = document.querySelector(`[data-filter-attr="${attr}"].filter-range`);
			const multiselectEl = document.querySelector(`[data-filter-attr="${attr}"].filter-multiselect`);
			const buttonGroupEl = document.querySelector(`[data-filter-attr="${attr}"].filter-button-group`);
			const searchEl = document.querySelector(`[data-filter-attr="${attr}"].filter-search`);

			if(rangeEl && value.includes('-') && !isNaN(value.split('-')[0])) {
				const [min, max] = value.split('-').map(v => parseInt(v));
				if(!isNaN(min) && !isNaN(max)) {
					this.activeFilters[attr] = {
						type: 'range',
						min: min,
						max: max
					};
				}
			}
			else if(multiselectEl) {
				this.activeFilters[attr] = {
					type: 'multiselect',
					values: value.split(',').map(v => v.trim())
				};
			}
			else if(buttonGroupEl) {
				this.activeFilters[attr] = {
					type: 'button-group',
					values: value.split(',').map(v => v.trim())
				};
			}
			else if(searchEl) {
				this.activeFilters[attr] = {
					type: attr === 'all' ? 'global-search' : 'search',
					value: value.toLowerCase()
				};
			}
		});
	}

	syncFiltersFromURL() {
		document.querySelectorAll('.filter-range').forEach((slider) => {
			const attr = slider.dataset.filterAttr;
			const sliderEl = slider.querySelector('.filter-range__slider');
			const minThumb = slider.querySelector('.filter-range__thumb--min');
			const maxThumb = slider.querySelector('.filter-range__thumb--max');
			const fill = slider.querySelector('.filter-range__fill');
			const minDisplay = slider.querySelector('.filter-range__value--min');
			const maxDisplay = slider.querySelector('.filter-range__value--max');

			if(this.activeFilters[attr]?.type === 'range' && sliderEl) {
				const filter = this.activeFilters[attr];
				const min = parseInt(sliderEl.dataset.min);
				const max = parseInt(sliderEl.dataset.max);

				const minPercent = ((filter.min - min) / (max - min)) * 100;
				const maxPercent = ((filter.max - min) / (max - min)) * 100;

				if(minThumb) minThumb.style.left = minPercent + '%';
				if(maxThumb) maxThumb.style.left = maxPercent + '%';
				if(fill) {
					fill.style.left = minPercent + '%';
					fill.style.width = (maxPercent - minPercent) + '%';
				}
				if(minDisplay) minDisplay.textContent = filter.min;
				if(maxDisplay) maxDisplay.textContent = filter.max;
			} else if(sliderEl && !this.activeFilters[attr]) {
				// Reset to full range when no filter active
				const min = parseInt(sliderEl.dataset.min);
				const max = parseInt(sliderEl.dataset.max);

				if(minThumb) minThumb.style.left = '0%';
				if(maxThumb) maxThumb.style.left = '100%';
				if(fill) {
					fill.style.left = '0%';
					fill.style.width = '100%';
				}
				if(minDisplay) minDisplay.textContent = min;
				if(maxDisplay) maxDisplay.textContent = max;
			}
		});

		document.querySelectorAll('.filter-multiselect').forEach((container) => {
			const attr = container.dataset.filterAttr;

			if(!container._tracked || !container._updateChips) return;

			container._tracked.clear();

			if(this.activeFilters[attr]?.type === 'multiselect') {
				this.activeFilters[attr].values.forEach(v => container._tracked.add(v));
			}

			const select = container.querySelector('.filter-multiselect__select');
			if(select) select.selectedIndex = 0;

			container._updateChips();
		});

		document.querySelectorAll('.filter-button-group').forEach((container) => {
			const attr = container.dataset.filterAttr;
			const buttons = container.querySelectorAll('.filter-button-group__button');

			if(this.activeFilters[attr]?.type === 'button-group' && buttons.length) {
				const selectedValues = this.activeFilters[attr].values;
				buttons.forEach(btn => {
					if(selectedValues.includes(btn.dataset.value)) {
						btn.classList.add('is-active');
					} else {
						btn.classList.remove('is-active');
					}
				});
			}
		});

		document.querySelectorAll('.filter-search').forEach((container) => {
			const attr = container.dataset.filterAttr;
			const input = container.querySelector('.filter-search__input');
			if(this.activeFilters[attr] && input) {
				input.value = this.activeFilters[attr].value;
			}
		});

		this.updateFilterButtons();
		this.updateViewResultsButton();
		this.updateResetLinks();
	}

	updateURL() {
		const urlParams = new URLSearchParams(window.location.search);

		const keysToDelete = [];
		urlParams.forEach((value, key) => {
			if(key.startsWith('view_')) {
				keysToDelete.push(key);
			}
		});
		keysToDelete.forEach(key => urlParams.delete(key));

		for(const [attr, filter] of Object.entries(this.activeFilters)) {
			const paramName = `view_${attr}`;

			if(filter.type === 'range') {
				urlParams.set(paramName, `${filter.min}-${filter.max}`);
			} else if(filter.type === 'multiselect' || filter.type === 'button-group') {
				urlParams.set(paramName, filter.values.join(','));
			} else if(filter.type === 'search') {
				urlParams.set(paramName, filter.value);
			}
		}

		const newURL = urlParams.toString()
			? `${window.location.pathname}?${urlParams.toString()}`
			: window.location.pathname;

		window.history.replaceState({}, '', newURL);
	}

	reloadTimelinePage() {
		window.location.reload();
	}
}
